import { type Material, formatAmount, materialLabel } from '@/components/sklad/materialTypes';
import { matches, tokenize } from '@/components/sklad/types';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Výběr položky drobného materiálu z katalogu — hledání přes druh, název,
 * barvu, velikost i kód dodavatele. U každé položky je vidět aktuální stav.
 */
export function MaterialPicker({
  value,
  options,
  loading,
  onChange,
}: {
  value: Material | null;
  options: Material[];
  loading: boolean;
  onChange: (m: Material) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Klik mimo dropdown ho zavře.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const tokens = tokenize(query);
    return options.filter(
      (o) =>
        !o.archived_at && matches(`${o.category} ${o.name} ${o.color} ${o.size} ${o.code}`, tokens),
    );
  }, [options, query]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 text-left text-sm bg-surface border border-border rounded-lg px-2.5 py-2 min-h-[42px] hover:border-primary focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition-all cursor-pointer"
      >
        {value ? (
          <span className="flex-1 min-w-0 truncate text-text font-medium">
            {value.category && <span className="text-text-muted">{value.category}: </span>}
            {materialLabel(value)}
          </span>
        ) : (
          <span className="flex-1 text-text-muted">
            {loading ? 'Načítám materiál…' : '— vyber materiál —'}
          </span>
        )}
        <span className="text-text-muted shrink-0">▾</span>
      </button>

      {open && (
        <div className="absolute z-10 mt-1 w-full bg-surface border border-border rounded-lg shadow-card-hover overflow-hidden">
          <div className="p-2 border-b border-border-light">
            <input
              // biome-ignore lint/a11y/noAutofocus: dropdown se otevírá kliknutím, hledání je jeho účel
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Hledat druh, název, barvu, velikost…"
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
            />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading && (
              <div className="px-3 py-6 text-center text-sm text-text-muted">Načítám…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-text-muted">
                Nic nenalezeno — novou položku založ ve Skladu drobného materiálu
              </div>
            )}
            {!loading &&
              filtered.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => {
                    onChange(o);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-surface-hover transition-colors cursor-pointer"
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-text truncate">{materialLabel(o)}</span>
                    <span className="block text-[11px] text-text-muted truncate">
                      {o.category || 'Bez druhu'}
                      {o.code && ` · ${o.code}`}
                    </span>
                  </span>
                  <span className="text-xs text-text-secondary shrink-0">
                    skladem {formatAmount(o.stock_qty, o.unit)}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
